// Shared "is this vehicle's check due" computation -- used by
// VehicleInspection.tsx (the actual check wizard) and
// useDriverVehicleCheckStatus.ts (the driver dashboard's countdown/lockout
// badge), so the two never drift out of sync with each other.
import { supabase } from "@/lib/supabase";

export interface VehicleCheckDueResult {
  isDue: boolean; // days OR shifts, whichever comes first
  daysDue: boolean;
  shiftsDue: boolean;
  daysSinceLastCheck: number | null;
  completedShiftsSinceLastCheck: number;
  lastInspectionAt: string | null;
  intervalDays: number;
  shiftThreshold: number;
  reason: string;
}

export async function computeVehicleCheckDue(
  vehicleNumber: string,
  intervalDays: number,
  shiftThreshold: number
): Promise<VehicleCheckDueResult> {
  // COMPLETED inspections only -- created_at is set the moment the wizard
  // opens, before any checklist/photos exist, so a driver who starts and
  // abandons one must never count as "checked". See
  // vehicle_inspections.completed_at.
  const { data: lastInspection, error: inspError } = await supabase
    .from("vehicle_inspections")
    .select("id, completed_at")
    .eq("vehicle_number", vehicleNumber)
    .not("completed_at", "is", null)
    .order("completed_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (inspError) console.error("Error checking last inspection:", inspError);

  let completedShiftsSince = 0;
  let lastInspectionStamp: string | null = null;
  let daysSinceLastCheck: number | null = null;

  if (lastInspection?.completed_at) {
    lastInspectionStamp = lastInspection.completed_at;

    const lastDate = new Date(lastInspectionStamp);
    const lastISODate = `${lastDate.getFullYear()}-${String(lastDate.getMonth() + 1).padStart(2, "0")}-${String(
      lastDate.getDate()
    ).padStart(2, "0")}`;

    daysSinceLastCheck = Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    const { count, error: countError } = await supabase
      .from("driver_shifts")
      .select("id", { count: "exact", head: true })
      .eq("vehicle_number", vehicleNumber)
      .eq("shift_status", "completed")
      .gt("shift_date", lastISODate);

    if (!countError && count !== null) completedShiftsSince = count;
  } else {
    const { count, error: countError } = await supabase
      .from("driver_shifts")
      .select("id", { count: "exact", head: true })
      .eq("vehicle_number", vehicleNumber)
      .eq("shift_status", "completed");

    if (!countError && count !== null) completedShiftsSince = count;
  }

  const shiftsDue = completedShiftsSince >= shiftThreshold;
  const daysDue = daysSinceLastCheck !== null && daysSinceLastCheck >= intervalDays;
  const isDue = !lastInspection || shiftsDue || daysDue;

  let reason: string;
  if (!lastInspection) {
    reason = "No previous inspection recorded";
  } else if (daysDue && shiftsDue) {
    reason = `${daysSinceLastCheck} day${daysSinceLastCheck === 1 ? "" : "s"} and ${completedShiftsSince} shifts since last inspection`;
  } else if (daysDue) {
    reason = `${daysSinceLastCheck} day${daysSinceLastCheck === 1 ? "" : "s"} since last inspection`;
  } else if (shiftsDue) {
    reason = `${completedShiftsSince} shifts completed since last inspection`;
  } else {
    reason = `Not due yet (${completedShiftsSince}/${shiftThreshold} shifts, ${daysSinceLastCheck}/${intervalDays} days since last inspection)`;
  }

  return {
    isDue,
    daysDue,
    shiftsDue,
    daysSinceLastCheck,
    completedShiftsSinceLastCheck: completedShiftsSince,
    lastInspectionAt: lastInspectionStamp,
    intervalDays,
    shiftThreshold,
    reason,
  };
}
